import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Validate environment variables at startup
const isConfigured = Boolean(supabaseUrl && supabaseAnonKey && !supabaseUrl.includes('placeholder'));

if (!isConfigured) {
  console.error('[Supabase] MISSING OR INVALID ENVIRONMENT VARIABLES!');
  console.error('[Supabase] Ensure .env file has:');
  console.error('  VITE_SUPABASE_URL=https://your-project.supabase.co');
  console.error('  VITE_SUPABASE_ANON_KEY=your-anon-key');
}

/**
 * Per-tab async lock for Supabase auth/session operations.
 *
 * Supabase's default lock uses the Web Locks API (navigator.locks), which is
 * scoped to the whole origin and shared across every tab/context. In Safari,
 * when a tab is frozen for the back/forward cache (bfcache) and later restored,
 * that global lock can be left held by the orphaned context and is never
 * released. The next getSession()/query then blocks forever — which is exactly
 * why Flow would hang on "Verifying access…" when revisited and only recover
 * after a full reload.
 *
 * A lock that lives in this tab's JS heap can never be held by a dead context,
 * so it cannot deadlock across tabs or bfcache restores. We still serialize auth
 * operations within the tab (to avoid concurrent token refreshes), but if a
 * prior operation is wedged for longer than `acquireTimeout` we proceed anyway
 * instead of hanging.
 */
const authLockChain: Record<string, Promise<unknown>> = {};
async function inTabAuthLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  const previous = authLockChain[name] ?? Promise.resolve();
  const ready =
    acquireTimeout >= 0
      ? Promise.race([
          previous.catch(() => undefined),
          new Promise<void>((resolve) => setTimeout(resolve, acquireTimeout)),
        ])
      : previous.catch(() => undefined);

  const run = ready.then(() => fn());
  authLockChain[name] = run.catch(() => undefined);
  return run;
}

// Create client (will fail gracefully if not configured)
export const supabase: SupabaseClient = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Use a per-tab lock instead of the default cross-origin Web Locks lock,
      // which deadlocks in Safari on bfcache restore (see inTabAuthLock above).
      lock: inTabAuthLock,
    },
  }
);

// Export configuration status for components to check
export const isSupabaseConfigured = isConfigured;
